import * as THREE from 'three';
import { palette } from './palette';
import { getStarSprite } from './textures';

export interface StarfieldOptions {
  readonly starCount?: number;
  readonly dustCount?: number;
  readonly nebulaCount?: number;
  /** Multiplier for all layer radii — bump for bigger sectors. */
  readonly scale?: number;
}

interface StarLayer {
  readonly count: number;
  readonly inner: number;
  readonly outer: number;
  readonly baseSize: number;
  readonly sizeVariance: number;
  readonly opacity: number;
  readonly pool: readonly THREE.Color[];
  readonly additive: boolean;
}

export function createStarfield(options: StarfieldOptions = {}): THREE.Group {
  const group = new THREE.Group();
  group.name = 'starfield';
  group.frustumCulled = false;

  const warm = new THREE.Color(palette.star.warm);
  const cool = new THREE.Color(palette.star.cool);
  const soft = new THREE.Color(palette.star.soft);
  const gold = new THREE.Color(palette.star.gold);
  const pink = new THREE.Color(palette.star.pink);

  const dustCount = options.dustCount ?? 5200;
  const starCount = options.starCount ?? 1400;
  const nebulaCount = options.nebulaCount ?? 180;
  const scale = options.scale ?? 1;

  const layers: StarLayer[] = [
    {
      count: dustCount,
      inner: 900 * scale,
      outer: 5200 * scale,
      baseSize: 1.2,
      sizeVariance: 0.8,
      opacity: 0.75,
      pool: [soft, soft, soft, cool, warm],
      additive: false
    },
    {
      count: starCount,
      inner: 700 * scale,
      outer: 4200 * scale,
      baseSize: 2.8,
      sizeVariance: 1.6,
      opacity: 0.9,
      pool: [soft, cool, warm, gold],
      additive: true
    },
    {
      count: nebulaCount,
      inner: 600 * scale,
      outer: 3200 * scale,
      baseSize: 7.5,
      sizeVariance: 3.5,
      opacity: 1.0,
      pool: [gold, pink, cool, warm],
      additive: true
    }
  ];

  const sprite = getStarSprite();

  for (const layer of layers) {
    group.add(buildLayer(layer, sprite));
  }

  // The starfield never moves or rotates — bake the matrix once and stop the
  // scene graph from recomputing it every frame for every layer.
  group.updateMatrix();
  group.matrixAutoUpdate = false;
  for (const child of group.children) {
    child.updateMatrix();
    child.matrixAutoUpdate = false;
  }

  return group;
}

function buildLayer(
  layer: StarLayer,
  sprite: THREE.Texture
): THREE.Points {
  const positions = new Float32Array(layer.count * 3);
  const colors = new Float32Array(layer.count * 3);
  const sizes = new Float32Array(layer.count);
  const fallback = layer.pool[0]!;

  for (let i = 0; i < layer.count; i++) {
    const r = layer.inner + Math.cbrt(Math.random()) * (layer.outer - layer.inner);
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const base = i * 3;

    positions[base] = r * Math.sin(phi) * Math.cos(theta);
    positions[base + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[base + 2] = r * Math.cos(phi);

    const picked =
      layer.pool[Math.floor(Math.random() * layer.pool.length)] ?? fallback;
    const intensity = 0.6 + Math.random() * 0.45;
    colors[base] = picked.r * intensity;
    colors[base + 1] = picked.g * intensity;
    colors[base + 2] = picked.b * intensity;

    sizes[i] = layer.baseSize + Math.random() * layer.sizeVariance;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);

  const material = new THREE.PointsMaterial({
    map: sprite,
    size: layer.baseSize,
    sizeAttenuation: true,
    vertexColors: true,
    transparent: true,
    opacity: layer.opacity,
    depthWrite: false,
    fog: false,
    blending: layer.additive ? THREE.AdditiveBlending : THREE.NormalBlending
  });

  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;
  return points;
}
